"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.sync = exports.scanAccounts = void 0;
const jsHelpers_1 = require("../../bridge/jsHelpers");
const jsHelpers_2 = require("../../bridge/jsHelpers");
const account_1 = require("../../account");
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const utils_1 = require("@cardano-foundation/ledgerjs-hw-app-cardano/dist/utils");
const typhonjs_1 = require("@stricahq/typhonjs");
const logic_1 = require("./logic");
const operation_1 = require("../../operation");
const networks_1 = require("./networks");
const getNetworkInfo_1 = require("./api/getNetworkInfo");
const uniqBy_1 = __importDefault(require("lodash/uniqBy"));
const postSyncPatch_1 = __importDefault(require("./postSyncPatch"));
const getTransactions_1 = require("./api/getTransactions");
const buildSubAccounts_1 = require("./buildSubAccounts");
const utils_2 = require("@stricahq/typhonjs/dist/utils/utils");
const currencies_1 = require("../../currencies");
function mapTxToAccountOperation(tx, accountId, accountCredentialsMap, subAccounts) {
    const accountChange = (0, logic_1.getAccountChange)(tx, accountCredentialsMap);
    const mainOperationType = (0, logic_1.getOperationType)({
        valueChange: accountChange.ada,
        fees: new bignumber_js_1.default(tx.fees),
    });
    const subOperations = (0, account_1.inferSubOperations)(tx.hash, subAccounts);
    const memo = (0, logic_1.getMemoFromTx)(tx);
    const extra = {};
    if (memo) {
        extra["memo"] = memo;
    }
    return {
        accountId,
        id: (0, operation_1.encodeOperationId)(accountId, tx.hash, mainOperationType),
        hash: tx.hash,
        type: mainOperationType,
        fee: new bignumber_js_1.default(tx.fees),
        value: accountChange.ada.absoluteValue(),
        senders: tx.inputs.map(i => (0, logic_1.isHexString)(i.address) ? typhonjs_1.utils.getAddressFromHex(i.address).getBech32() : i.address),
        recipients: tx.outputs.map(o => (0, logic_1.isHexString)(o.address) ? typhonjs_1.utils.getAddressFromHex(o.address).getBech32() : o.address),
        subOperations,
        blockHeight: tx.blockHeight,
        date: new Date(tx.timestamp),
        extra: extra,
        blockHash: undefined,
    };
}
function prepareUtxos(newTransactions, stableUtxos, accountCredentialsMap) {
    const newUtxos = [];
    // spentUtxoKey = txId#index
    const spentUtxoKeys = new Set();
    newTransactions.forEach(t => {
        t.inputs.forEach(i => {
            const cred = accountCredentialsMap[i.paymentKey];
            if (cred)
                spentUtxoKeys.add(`${i.txId}#${i.index}`);
        });
        t.outputs.forEach((o, outputIndex) => {
            const cred = accountCredentialsMap[o.paymentKey];
            if (cred) {
                newUtxos.push({
                    hash: t.hash,
                    index: outputIndex,
                    address: o.address,
                    amount: new bignumber_js_1.default(o.value),
                    tokens: o.tokens.map(token => ({
                        assetName: token.assetName,
                        policyId: token.policyId,
                        amount: new bignumber_js_1.default(token.value),
                    })),
                    paymentCredential: {
                        key: o.paymentKey,
                        path: cred.path,
                    },
                });
            }
        });
    });
    const utxos = (0, uniqBy_1.default)([...stableUtxos, ...newUtxos], u => `${u.hash}#${u.index}`).filter(u => !spentUtxoKeys.has(`${u.hash}#${u.index}`));
    return utxos;
}
const makeGetAccountShape = (signerContext) => (info, { blacklistedTokenIds }) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const { currency, index: accountIndex, derivationPath, derivationMode, initialAccount, deviceId, } = info;
    // In case we get a full derivation path
    const rootPath = derivationPath.split("/", 2).join("/");
    const accountPath = `${rootPath}/${accountIndex}'`;
    const paramXpub = initialAccount === null || initialAccount === void 0 ? void 0 : initialAccount.xpub;
    let extendedPubKeyRes;
    if (!paramXpub) {
        if (deviceId === undefined || deviceId === null) {
            // deviceId not provided
            throw new Error("deviceId required to generate the xpub");
        }
        extendedPubKeyRes = yield signerContext(deviceId, signer => signer.getExtendedPublicKey({
            path: (0, utils_1.str_to_path)(accountPath),
        }));
    }
    const xpub = paramXpub || `${extendedPubKeyRes.publicKeyHex}${extendedPubKeyRes.chainCodeHex}`;
    const accountId = (0, account_1.encodeAccountId)({
        type: "js",
        version: "2",
        currencyId: currency.id,
        xpubOrAddress: xpub,
        derivationMode,
    });
    // when new tokens are added / blacklist changes, we need to sync again because we need to go through all operations again
    const syncHash = JSON.stringify(blacklistedTokenIds || []) +
        "_" +
        (0, currencies_1.listTokensForCryptoCurrency)(currency, {
            withDelisted: true,
        }).length;
    const outdatedSyncHash = (initialAccount === null || initialAccount === void 0 ? void 0 : initialAccount.syncHash) !== syncHash;
    const requiredConfirmations = 90;
    const oldOperations = (initialAccount === null || initialAccount === void 0 ? void 0 : initialAccount.operations) || [];
    const lastBlockHeight = oldOperations.length ? (oldOperations[0].blockHeight || 0) + 1 : 0;
    const syncFromBlockHeight = outdatedSyncHash
        ? 0
        : lastBlockHeight > requiredConfirmations
            ? lastBlockHeight - requiredConfirmations
            : 0;
    const { transactions: newTransactions, blockHeight, externalCredentials, internalCredentials, } = yield (0, getTransactions_1.getTransactions)(xpub, accountIndex, initialAccount, syncFromBlockHeight, currency);
    const accountCredentialsMap = [...externalCredentials, ...internalCredentials].reduce((finalMap, cred) => {
        finalMap[cred.key] = cred;
        return finalMap;
    }, {});
    const stableOperationsByIds = {};
    ((initialAccount === null || initialAccount === void 0 ? void 0 : initialAccount.operations) || []).forEach(o => {
        if (o.blockHeight < syncFromBlockHeight) {
            stableOperationsByIds[o.hash] = o;
        }
    });
    const stableUtxos = (((_a = initialAccount === null || initialAccount === void 0 ? void 0 : initialAccount.cardanoResources) === null || _a === void 0 ? void 0 : _a.utxos) || []).filter(u => stableOperationsByIds[u.hash]);
    const utxos = prepareUtxos(newTransactions, stableUtxos, accountCredentialsMap);
    const accountBalance = utxos.reduce((total, u) => total.plus(u.amount), new bignumber_js_1.default(0));
    const tokenBalance = (0, logic_1.mergeTokens)(utxos.map(u => u.tokens).flat());
    const subAccounts = (0, buildSubAccounts_1.buildSubAccounts)({
        initialAccount,
        parentAccountId: accountId,
        parentCurrency: currency,
        newTransactions,
        tokens: tokenBalance,
        accountCredentialsMap,
    }).filter(a => !(blacklistedTokenIds === null || blacklistedTokenIds === void 0 ? void 0 : blacklistedTokenIds.includes(a.token.id)));
    const newOperations = newTransactions.map(t => mapTxToAccountOperation(t, accountId, accountCredentialsMap, subAccounts));
    const operations = (0, jsHelpers_1.mergeOps)(Object.values(stableOperationsByIds), newOperations);
    const stakeCredential = (0, logic_1.getAccountStakeCredential)(xpub, accountIndex);
    const networkParams = (0, networks_1.getNetworkParameters)(currency.id);
    const freshAddresses = externalCredentials
        .filter(c => !c.isUsed)
        .map(c => ({
        derivationPath: (0, logic_1.getBipPathString)(c.path),
        address: (0, logic_1.getBaseAddress)({
            networkId: networkParams.networkId,
            paymentCred: c,
            stakeCred: stakeCredential,
        }).getBech32(),
    }));
    const cardanoNetworkInfo = yield (0, getNetworkInfo_1.getNetworkInfo)(initialAccount, currency);
    const minAdaBalanceForTokens = tokenBalance.length
        ? (0, utils_2.calculateMinUtxoAmount)(tokenBalance, new bignumber_js_1.default(cardanoNetworkInfo.protocolParams.lovelacePerUtxoWord), false)
        : new bignumber_js_1.default(0);
    return {
        id: accountId,
        xpub,
        balance: accountBalance,
        spendableBalance: accountBalance.minus(minAdaBalanceForTokens),
        operations: operations,
        syncHash,
        subAccounts,
        freshAddresses,
        freshAddress: freshAddresses[0].address,
        freshAddressPath: freshAddresses[0].derivationPath,
        blockHeight,
        cardanoResources: {
            utxos,
            externalCredentials,
            internalCredentials,
            protocolParams: cardanoNetworkInfo.protocolParams,
        },
    };
});
const scanAccounts = (signerContext) => (0, jsHelpers_1.makeScanAccounts)({ getAccountShape: makeGetAccountShape(signerContext) });
exports.scanAccounts = scanAccounts;
const sync = (signerContext) => (0, jsHelpers_2.makeSync)({
    getAccountShape: makeGetAccountShape(signerContext),
    postSync: postSyncPatch_1.default,
});
exports.sync = sync;
//# sourceMappingURL=js-synchronisation.js.map